<template>
  <b-modal
    v-model="modal"
    class="position-relative"
    lazy
    centered
    body-class="py-5"
    content-class="new-section-modal"
    size="lg"
    hide-footer
    hide-header
  >
    <h3 class="text-center font-weight-bold mb-12 px-0">
      {{ blockId ? 'Update Block' : 'Upload New Block' }}
    </h3>

    <b-row>
      <b-col
        offset="2"
        cols="8"
      >
        <b-form @submit.prevent="onsubmit">
          <img
            v-if="form.thumbnail"
            :src="form.thumbnail"
            alt="Template Thumbnail"
            class="w-full bg-[#D9D9D9] border border-[#E2E5EC] rounded-[5px] object-cover aspect-video"
          >
          <div
            v-else
            class="w-full bg-[#D9D9D9] border border-[#E2E5EC] rounded-[5px] flex items-center justify-center aspect-video"
          />
          
          <upload-asset
            type="jpg|png|jpeg|blob|webp"
            @uploaded="e => insertImage(e.url)"
          >
            <template #trigger>
              <div class="inline-flex items-center mt-[12px]">
                <label class="cursor-pointer inline-flex items-center">
                  <svg
                    class="mr-2 w-4 h-4"
                    width="14"
                    height="12"
                    viewBox="0 0 14 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.16667 8.47463C1.46317 8.00373 1 7.20179 1 6.29167C1 4.92458 2.04505 3.80159 3.37985 3.67797C3.65289 2.01708 5.09514 0.75 6.83333 0.75C8.57152 0.75 10.0138 2.01708 10.2868 3.67797C11.6216 3.80159 12.6667 4.92458 12.6667 6.29167C12.6667 7.20179 12.2035 8.00373 11.5 8.47463M4.5 8.33333L6.83333 6M6.83333 6L9.16667 8.33333M6.83333 6V11.25"
                      stroke="black"
                      stroke-width="1.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <span class="text-[#44474A]">
                    Upload Thumbnail
                  </span>
                </label>
              </div>
            </template>
          </upload-asset>
          
          <b-form-group
            class="estage-form-group mt-4"
            label="Enter Block Name"
          >
            <b-input
              v-model="form.name"
              v-validate="'required'"
              name="name"
              :state="errors.has('name') ? false : null"
            />
          </b-form-group>

          <b-form-group
            class="estage-form-group"
            label="Preview URL"
          >
            <b-input
              v-model="form.prev_url"
              v-validate="'required'"
              name="preview"
              :state="errors.has('preview') ? false : null"
            />
          </b-form-group>

          <b-form-group
            label="Group"
            class="estage-form-group"
          >
            <v-select
              v-model="form.group"
              :options="groups"
            />
          </b-form-group>

          <div class="flex items-center justify-center gap-5 mt-5">
            <b-btn
              type="submit"
              variant="primary"
              class="font-weight-bold font-size-14 px-5"
              :disabled="loading"
            >
              {{ blockId ? 'Update' : 'Upload' }}

              <b-spinner
                v-if="loading"
                variant="white"
                small
                class="ml-2"
              />
            </b-btn>

            <b-btn
              v-if="blockId"
              variant="danger"
              @click="deleteBlock"
            >
              Delete
            </b-btn>
          </div>
        </b-form>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import {mapState} from 'vuex'
import html2canvas from 'html2canvas'
import UploadAsset
  from '@/components/editor/components/sid-controls/sid-control-pressets/background-image/UploadAsset.vue'

export default {
  name: 'AddToBlocksModal',
  components: {UploadAsset},
  props: {
    block: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      modal: false,
      loading: false,
      form: {
        thumbnail: '',
        name: '',
        group: '',
        prev_url: ''
      },
      groups: [
        'empty-blocks',
        'navbar',
        'hero',
        'featured',
        'blog',
        'resource',
        'call-to-actions',
        'opt-in-forms',
        'faq',
        'cards',
        'logos',
        'gallery',
        'showcase',
        'newsLetter',
        'contact-forms',
        'about-us',
        'testimonials',
        'pricing',
        'videos',
        'carousel',
        'quotes',
        'alert-bar',
        'footer',
        'others'
      ]
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapState('auth', {
      user: state => state.user
    }),
    blockId () {
      return this.block.original ? this.block.original.block_manage_id : false
    }
  },
  watch: {
    block (val) {
      if (Object.keys(val).length) {
        this.modal = true
        this.formSetup()
      }
    }
  },
  methods: {
    insertImage (file) {
      if (!file) return

      this.form.thumbnail = file
    },
    async takeScreenShot () {
      await html2canvas(document.querySelector(`.--${this.block.section.sid}--container`), {
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        logging: false
      }).then(canvas => {
        let croppedCanvas = document.createElement('canvas'),
          croppedCanvasContext = croppedCanvas.getContext('2d')

        croppedCanvas.width = canvas.width
        croppedCanvas.height = 1900

        croppedCanvasContext.drawImage(canvas, 0, 0, canvas.width, 1900, 0, 0, canvas.width, 1900)

        this.form.thumbnail = croppedCanvas.toDataURL()
      })
    },
    formSetup () {
      if (this.blockId) {
        axios.get(`api/projects/block-management/${this.blockId}`)
          .then(({data}) => {
            this.form = {
              name: data.data.name,
              group: data.data.group,
              prev_url: `https://${this.project.subdomain}.estage.site`
            }
          })
      } else {
        this.form = {
          thumbnail: '',
          name: this.block.section.name,
          group: '',
          prev_url: `https://${this.project.subdomain}.estage.site`
        }
        this.takeScreenShot()
      }
    },
    deleteBlock () {
      this.loading = true
      axios.delete(`api/projects/block-management/${this.blockId}`)
        .then(() => {
          this.$swal({
            icon: 'success',
            iconColor: '#4F83E3',
            toast: true,
            position: 'top-right',
            title: 'Deleted.',
            showConfirmButton: false,
            timer: 3000
          })

          delete this.block.original.block_manage_id
          this.$store.dispatch('pages/save')
          this.modal = false
        })
        .finally(() => {
          this.loading = false
        })
    },
    async onsubmit () {
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          this.loading = true

          try {
            let thumbnail = this.form.thumbnail

            if (thumbnail && !thumbnail.startsWith('data:image')) {
              try {
                const res = await fetch(thumbnail, { mode: 'cors' })
                const blob = await res.blob()

                // Convert blob → base64
                thumbnail = await new Promise((resolve, reject) => {
                  const reader = new FileReader()
                  reader.onloadend = () => resolve(reader.result)
                  reader.onerror = reject
                  reader.readAsDataURL(blob)
                })
              } catch (err) {
                console.error('Error converting thumbnail to base64:', err)
              }
            }

            const payload = {
              ...this.form,
              thumbnail,
              block: JSON.stringify(this.block.section)
            }

            const url = `api/projects/block-management${this.blockId ? '/' + this.blockId : ''}`
            const method = this.blockId ? 'put' : 'post'

            const { data } = await axios[method](url, payload)

            this.$swal({
              icon: 'success',
              iconColor: '#4F83E3',
              toast: true,
              position: 'top-right',
              title: 'Published!',
              showConfirmButton: false,
              timer: 3000
            })

            this.$set(this.block.original, 'block_manage_id', data.data.id)
            this.$store.dispatch('pages/save')
            this.modal = false
          } finally {
            this.loading = false
          }
        }
      })
    }
  }
}
</script>